import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiClock,
  FiMessageSquare,
  FiUserX,
  FiTrendingDown,
  FiCheckCircle,
} from 'react-icons/fi';

/**
 * Mentor-specific triage list. The backend ranks items by severity
 * ("high" / "medium" / "low"), so we render them in that order and surface
 * an icon + tone-matched chip per reason.
 */
const REASON_CONFIG = {
  never_logged_in: { icon: FiUserX, label: 'Never signed in' },
  inactive_14d: { icon: FiClock, label: 'Inactive' },
  no_apps_30d: { icon: FiTrendingDown, label: 'No applications' },
  open_tickets: { icon: FiMessageSquare, label: 'Open ticket' },
  no_interviews: { icon: FiAlertTriangle, label: 'No interviews' },
};

const SEVERITY_STYLES = {
  high: 'bg-rose-50 border-rose-200 text-rose-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low: 'bg-gray-50 border-gray-200 text-gray-600',
};

const SEVERITY_DOT = {
  high: 'bg-rose-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
};

const AttentionRow = ({ item }) => {
  const config = REASON_CONFIG[item.reason] || { icon: FiAlertTriangle, label: item.reason };
  const Icon = config.icon;
  // Open-ticket items drop the mentor straight into the tickets queue; every
  // other reason is mentee-specific, so we link to the mentee profile.
  const to = item.reason === 'open_tickets'
    ? '/mentor/tickets'
    : `/mentor/mentees/${item.menteeId}`;
  const chipCls = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.low;
  const dotCls = SEVERITY_DOT[item.severity] || SEVERITY_DOT.low;

  return (
    <Link
      to={to}
      className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors"
    >
      <div className={`flex-shrink-0 w-1.5 self-stretch rounded-full ${dotCls}`} />
      <div className="flex-shrink-0 p-1.5 bg-white border border-gray-200 rounded-md">
        <Icon className="h-4 w-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 truncate">{item.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${chipCls}`}>
            {config.label}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 leading-snug">{item.detail}</p>
      </div>
    </Link>
  );
};

const NeedsAttentionPanel = ({ items = [] }) => {
  const safeItems = Array.isArray(items) ? items : [];
  const highCount = safeItems.filter((i) => i.severity === 'high').length;

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-rose-500 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Needs Attention</h3>
          <p className="text-xs text-gray-500">Mentees and tickets to triage this week</p>
        </div>
        {safeItems.length > 0 && (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
            {highCount} high
          </span>
        )}
      </div>

      {safeItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center flex-1">
          <div className="p-2 rounded-full bg-emerald-50 mb-2">
            <FiCheckCircle className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-sm font-semibold text-gray-900">All clear</p>
          <p className="text-xs text-gray-500 mt-1">
            Nothing flagged. Mentees are active and tickets are resolved.
          </p>
        </div>
      ) : (
        <div className="space-y-2 overflow-y-auto flex-1 pr-1" style={{ maxHeight: 420 }}>
          {safeItems.map((item, i) => (
            <AttentionRow key={`${item.menteeId}-${item.reason}-${i}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default NeedsAttentionPanel;
